import { Fragment } from "react";
import { Link } from "react-router-dom";
import { motion, type Variants } from "framer-motion";
import {
  Shield,
  Users,
  UserPlus,
  Activity,
  Lock,
  ArrowRight,
  Layers,
  Globe,
  Eye,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { ParticleNetwork } from "@/components/Landing/ParticleNetwork";

// ─── Animation Variants ─────────────────────────────────────────

const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 35 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.25, 0.1, 0.25, 1] },
  },
};

const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.7, ease: [0.25, 0.1, 0.25, 1] },
  },
};

const staggerChildren: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

// ─── Data ────────────────────────────────────────────────────────

interface Feature {
  icon: LucideIcon;
  title: string;
  desc: string;
}

const FEATURES: Feature[] = [
  {
    icon: Shield,
    title: "Role-Based Access Control",
    desc: "Define granular roles and permissions to control who can access what across your AI ecosystem.",
  },
  {
    icon: Layers,
    title: "Application Onboarding",
    desc: "Seamlessly register and configure new AI applications with an intuitive onboarding workflow.",
  },
  {
    icon: Users,
    title: "User Management",
    desc: "Manage users, groups, and organizational hierarchies from a centralized admin dashboard.",
  },
  {
    icon: UserPlus,
    title: "Self Signup",
    desc: "Enable users to self-register with approval workflows, reducing admin overhead.",
  },
  {
    icon: Activity,
    title: "Audit Logs Tracking",
    desc: "Comprehensive logging of every action, login, and change for complete accountability.",
  },
  {
    icon: Lock,
    title: "Secure Architecture",
    desc: "Enterprise-grade security with encryption, MFA, and zero-trust principles built in.",
  },
];

interface Step {
  icon: LucideIcon;
  title: string;
  desc: string;
}

const STEPS: Step[] = [
  { icon: Shield, title: "Admin Creates Roles", desc: "Define access levels and permission groups" },
  { icon: Layers, title: "Applications Connect", desc: "Register AI apps and assign role requirements" },
  { icon: Users, title: "Users Attach to Roles", desc: "Users sign up and get role-based app access" },
  { icon: Activity, title: "Audit Monitors All", desc: "Every action is tracked and logged in real-time" },
];

interface Benefit {
  icon: LucideIcon;
  title: string;
  desc: string;
}

const BENEFITS: Benefit[] = [
  { icon: Globe, title: "Centralized AI Ecosystem", desc: "Unified platform for all your AI applications and services" },
  { icon: Shield, title: "Secure Access Control", desc: "Enterprise-grade security at every layer of the platform" },
  { icon: Zap, title: "Easy Onboarding", desc: "Get new apps and users started in minutes, not days" },
  { icon: Eye, title: "Full Transparency", desc: "Complete visibility into all platform activity and usage" },
];

const MOCK_APPS = [
  { name: "Smart Summarizer", status: "Active" },
  { name: "Code Assistant", status: "Active" },
  { name: "Doc Analyzer", status: "Beta" },
  { name: "Image Generator", status: "Paused" },
];

const MOCK_USERS = [
  { name: "Sarah Chen", initials: "SC", role: "Admin", status: "online" },
  { name: "Mike Johnson", initials: "MJ", role: "Editor", status: "online" },
  { name: "Emma Wilson", initials: "EW", role: "Viewer", status: "away" },
];

const MOCK_LOGS = [
  { action: "User login", detail: "sarah.chen", time: "2 min ago" },
  { action: "App accessed", detail: "Code Assistant", time: "5 min ago" },
  { action: "Role assigned", detail: "Editor → mike.j", time: "12 min ago" },
  { action: "App registered", detail: "Doc Analyzer", time: "1 hr ago" },
];

const HERO_PARTICLES = Array.from({ length: 10 }, (_, i) => ({
  left: 8 + i * 9 + (i % 3) * 3,
  top: 8 + ((i * 13) % 75) + (i % 2) * 8,
  dx: ((i % 3) - 1) * 22,
  dy: (i % 2 === 0 ? 1 : -1) * 16,
  dur: 3.5 + i * 0.5,
  delay: i * 0.3,
}));

const ELLIPSE_RATIO = 0.42;

const ATOM_ORBITS = [
  { angle: 30, dur: 12 },
  { angle: 90, dur: 16 },
  { angle: 150, dur: 14 },
];

const ATOM_ELECTRONS = [
  { orbitIdx: 0, startAngle: 0, dir: 1 },
  { orbitIdx: 1, startAngle: 120, dir: -1 },
  { orbitIdx: 2, startAngle: 240, dir: 1 },
];

function buildOrbitPath(orbitAngle: number, startAngle: number, dir: number) {
  const a = (orbitAngle * Math.PI) / 180;
  const ca = Math.cos(a);
  const sa = Math.sin(a);
  const rx = 44;
  const ry = 44 * ELLIPSE_RATIO;
  const N = 48;
  const lefts: string[] = [];
  const tops: string[] = [];
  for (let i = 0; i <= N; i++) {
    const t = ((startAngle + dir * (i / N) * 360) * Math.PI) / 180;
    const lx = rx * Math.cos(t);
    const ly = ry * Math.sin(t);
    lefts.push(`${+(50 + lx * ca - ly * sa).toFixed(2)}%`);
    tops.push(`${+(50 + lx * sa + ly * ca).toFixed(2)}%`);
  }
  return { lefts, tops };
}

const ELECTRON_PATHS = ATOM_ELECTRONS.map((e) => {
  const orbit = ATOM_ORBITS[e.orbitIdx];
  return {
    ...buildOrbitPath(orbit.angle, e.startAngle, e.dir),
    dur: orbit.dur,
  };
});

// ─── Sub-components ──────────────────────────────────────────────

function HeroAtom() {
  return (
    <div className="relative w-[300px] h-[300px] md:w-[420px] md:h-[420px]">
      <div className="absolute inset-[-30%] rounded-full bg-gradient-to-r from-[#E16F42]/15 to-[#1F4989]/15 blur-3xl" />

      {/* Thick orbital paths matching the Fission logo */}
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 100 100"
        fill="none"
      >
        <defs>
          <filter
            id="orbit-glow"
            x="-20%"
            y="-20%"
            width="140%"
            height="140%"
          >
            <feGaussianBlur in="SourceGraphic" stdDeviation="2" />
          </filter>
        </defs>
        {ATOM_ORBITS.map((o, i) => (
          <g key={i}>
            <ellipse
              cx="50"
              cy="50"
              rx="44"
              ry={44 * ELLIPSE_RATIO}
              stroke="#4787c7"
              strokeWidth="2.5"
              opacity="0.25"
              transform={`rotate(${o.angle} 50 50)`}
              filter="url(#orbit-glow)"
            />
            <ellipse
              cx="50"
              cy="50"
              rx="44"
              ry={44 * ELLIPSE_RATIO}
              stroke="#4787c7"
              strokeWidth="1.2"
              opacity="0.7"
              transform={`rotate(${o.angle} 50 50)`}
            />
          </g>
        ))}
      </svg>

      {/* Subtle nucleus glow at orbit intersection */}
      <motion.div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 md:w-12 md:h-12 rounded-full z-[5]"
        animate={{ scale: [1, 1.2, 1], opacity: [0.35, 0.65, 0.35] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        style={{
          background:
            "radial-gradient(circle, rgba(31,73,137,0.7), transparent 70%)",
        }}
      />

      {/* Electrons — always-visible circles tracing elliptical paths */}
      {ELECTRON_PATHS.map((path, i) => (
        <motion.div
          key={`electron-${i}`}
          className="absolute z-10 rounded-full"
          style={{
            width: 22,
            height: 22,
            marginLeft: -11,
            marginTop: -11,
            background:
              "radial-gradient(circle at 35% 35%, #ff9a6c, #E16F42 50%, #c4572e)",
            boxShadow:
              "0 0 10px 2px #E16F42, 0 0 24px 4px #E16F4250",
          }}
          animate={{
            left: path.lefts,
            top: path.tops,
          }}
          transition={{
            duration: path.dur,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      ))}

      {/* Floating molecular particles */}
      {HERO_PARTICLES.map((p, i) => (
        <motion.div
          key={`particle-${i}`}
          className="absolute w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-white/30"
          style={{ left: `${p.left}%`, top: `${p.top}%` }}
          animate={{
            x: [0, p.dx, 0],
            y: [0, p.dy, 0],
            opacity: [0.15, 0.5, 0.15],
          }}
          transition={{
            duration: p.dur,
            repeat: Infinity,
            ease: "easeInOut",
            delay: p.delay,
          }}
        />
      ))}
    </div>
  );
}

function SectionDivider() {
  return (
    <div className="flex justify-center">
      <motion.div
        className="w-px h-16 bg-gradient-to-b from-transparent via-white/20 to-transparent"
        initial={{ scaleY: 0 }}
        whileInView={{ scaleY: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      />
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors =
    status === "Active" || status === "online"
      ? "bg-emerald-500/20 text-emerald-400"
      : status === "Beta" || status === "away"
        ? "bg-amber-500/20 text-amber-400"
        : "bg-red-500/20 text-red-400";
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${colors}`}>
      {status}
    </span>
  );
}

// ─── Main Landing Page ───────────────────────────────────────────

export function Landing() {
  const scrollToFeatures = () => {
    document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div
      className="relative min-h-screen text-white overflow-x-hidden font-sans"
      style={{
        background: "linear-gradient(135deg, hsl(15 83% 56%), hsl(210 97% 29%))",
      }}
    >
      <ParticleNetwork />

      {/* Dark overlay */}
      <div className="fixed inset-0 pointer-events-none z-[1]" style={{
        background: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0.6) 100%)",
      }} />

      {/* Animated gradient blobs + mesh grid */}
      <div className="fixed inset-0 pointer-events-none z-[2] overflow-hidden">
        <motion.div
          className="absolute -top-[300px] -left-[200px] w-[700px] h-[700px] rounded-full opacity-30"
          style={{ background: "radial-gradient(circle, rgba(225,111,66,0.35), transparent 70%)" }}
          animate={{ x: [0, 60, 0], y: [0, 40, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute -bottom-[300px] -right-[200px] w-[800px] h-[800px] rounded-full opacity-30"
          style={{ background: "radial-gradient(circle, rgba(31,73,137,0.4), transparent 70%)" }}
          animate={{ x: [0, -50, 0], y: [0, -40, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-[30%] left-[40%] w-[500px] h-[500px] rounded-full opacity-20"
          style={{ background: "radial-gradient(circle, rgba(225,111,66,0.25), transparent 70%)" }}
          animate={{ x: [0, -70, 0], y: [0, 50, 0] }}
          transition={{ duration: 30, repeat: Infinity, ease: "easeInOut" }}
        />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      {/* ─── Content ─────────────────────────────────────────── */}
      <div className="relative z-10">

        {/* ═══ 1. Hero Section ═══ */}
        <section className="relative min-h-screen flex items-center justify-center px-6 py-24 lg:py-0">
          <div className="max-w-7xl mx-auto w-full grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerChildren}
              className="text-center lg:text-left order-2 lg:order-1"
            >
              <motion.p
                variants={fadeInUp}
                className="inline-block text-xs md:text-sm uppercase tracking-[0.25em] text-[#E16F42] font-semibold mb-6 px-4 py-1.5 rounded-full border border-[#E16F42]/20 bg-[#E16F42]/5 backdrop-blur-sm"
              >
                Enterprise AI Platform
              </motion.p>
              <motion.h1
                variants={fadeInUp}
                className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-display font-bold tracking-tight mb-6 leading-[1.05]"
              >
                AI Portal —{" "}
                <span className="bg-gradient-to-r from-[#1F4989] to-white bg-clip-text text-transparent">
                  Your Intelligent Application Ecosystem
                </span>
              </motion.h1>
              <motion.p
                variants={fadeInUp}
                className="text-lg md:text-xl text-white/60 mb-10 max-w-xl lg:max-w-none mx-auto lg:mx-0 leading-relaxed"
              >
                Seamlessly manage users, roles, and AI applications in one
                unified platform
              </motion.p>
              <motion.div
                variants={fadeInUp}
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              >
                <Link
                  to="/auth/login"
                  className="group inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#E16F42] to-[#c4572e] text-white font-semibold text-lg py-4 px-8 rounded-2xl shadow-[0_0_30px_rgba(225,111,66,0.3)] hover:shadow-[0_0_50px_rgba(225,111,66,0.5)] transition-all duration-300 hover:scale-105"
                >
                  Get Started
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button
                  type="button"
                  onClick={scrollToFeatures}
                  className="inline-flex items-center justify-center gap-2 backdrop-blur-xl bg-white/[0.06] border border-white/[0.12] text-white font-semibold text-lg py-4 px-8 rounded-2xl hover:bg-white/[0.12] hover:border-white/[0.2] transition-all duration-300"
                >
                  Explore Platform
                </button>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
              className="order-1 lg:order-2 flex justify-center"
            >
              <HeroAtom />
            </motion.div>
          </div>
        </section>

        <SectionDivider />

        {/* ═══ 2. Features Section ═══ */}
        <section id="features" className="relative py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={staggerChildren}
              className="text-center mb-16 md:mb-20"
            >
              <motion.p
                variants={fadeInUp}
                className="text-sm uppercase tracking-[0.2em] text-[#E16F42] font-semibold mb-3"
              >
                Platform Capabilities
              </motion.p>
              <motion.h2
                variants={fadeInUp}
                className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-5"
              >
                Molecular Features
              </motion.h2>
              <motion.p
                variants={fadeInUp}
                className="text-lg md:text-xl text-white/50 max-w-3xl mx-auto"
              >
                Each feature is a vital node in your AI ecosystem, connected
                and working together
              </motion.p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={staggerChildren}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
            >
              {FEATURES.map((f, i) => (
                <motion.div
                  key={f.title}
                  variants={fadeInUp}
                  whileHover={{
                    y: -8,
                    transition: { duration: 0.3 },
                  }}
                  className="group relative backdrop-blur-xl bg-white/[0.04] border border-white/[0.08] rounded-2xl p-8 hover:bg-white/[0.08] hover:border-white/[0.15] transition-colors duration-300 hover:shadow-[0_8px_40px_rgba(225,111,66,0.1)]"
                >
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#E16F42]/20 to-[#1F4989]/20 border border-white/[0.08] flex items-center justify-center mb-5 group-hover:shadow-[0_0_25px_rgba(225,111,66,0.25)] transition-shadow duration-300">
                    <f.icon className="w-7 h-7 text-[#E16F42]" />
                  </div>
                  <motion.div
                    className="absolute top-6 right-6 w-2 h-2 rounded-full bg-[#E16F42]/60"
                    animate={{
                      scale: [1, 1.5, 1],
                      opacity: [0.4, 0.8, 0.4],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: i * 0.3,
                    }}
                  />
                  <h3 className="text-xl font-bold text-white mb-3">
                    {f.title}
                  </h3>
                  <p className="text-white/45 leading-relaxed">{f.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <SectionDivider />

        {/* ═══ 3. How It Works ═══ */}
        <section className="relative py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={staggerChildren}
              className="text-center mb-16 md:mb-20"
            >
              <motion.p
                variants={fadeInUp}
                className="text-sm uppercase tracking-[0.2em] text-[#E16F42] font-semibold mb-3"
              >
                Chain Reaction
              </motion.p>
              <motion.h2
                variants={fadeInUp}
                className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-5"
              >
                How It Works
              </motion.h2>
              <motion.p
                variants={fadeInUp}
                className="text-lg md:text-xl text-white/50 max-w-3xl mx-auto"
              >
                A molecular chain reaction that builds your AI ecosystem step
                by step
              </motion.p>
            </motion.div>

            {/* Desktop: horizontal */}
            <div className="hidden lg:grid lg:grid-cols-7 gap-0 items-start">
              {STEPS.map((s, i) => (
                <Fragment key={s.title}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{
                      delay: i * 0.25,
                      duration: 0.5,
                      type: "spring",
                      stiffness: 200,
                    }}
                    className="flex flex-col items-center text-center"
                  >
                    <div className="relative">
                      <motion.div
                        className="w-20 h-20 rounded-full bg-gradient-to-br from-[#E16F42] to-[#1F4989] flex items-center justify-center shadow-lg"
                        animate={{
                          boxShadow: [
                            "0 0 20px rgba(225,111,66,0.3)",
                            "0 0 40px rgba(225,111,66,0.5)",
                            "0 0 20px rgba(225,111,66,0.3)",
                          ],
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          delay: i * 0.5,
                        }}
                      >
                        <s.icon className="w-8 h-8 text-white" />
                      </motion.div>
                      <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-xs font-bold">
                        {i + 1}
                      </div>
                    </div>
                    <h3 className="mt-5 text-lg font-bold text-white">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm text-white/45 max-w-[180px]">
                      {s.desc}
                    </p>
                  </motion.div>
                  {i < STEPS.length - 1 && (
                    <div className="flex items-center h-20">
                      <motion.div
                        className="w-full h-[2px] relative overflow-hidden rounded-full"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.25 + 0.2 }}
                      >
                        <div className="absolute inset-0 bg-white/[0.06]" />
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-[#E16F42] to-[#1F4989]"
                          initial={{ scaleX: 0 }}
                          whileInView={{ scaleX: 1 }}
                          viewport={{ once: true }}
                          transition={{
                            delay: i * 0.25 + 0.4,
                            duration: 0.5,
                          }}
                          style={{ transformOrigin: "left" }}
                        />
                      </motion.div>
                    </div>
                  )}
                </Fragment>
              ))}
            </div>

            {/* Mobile: vertical */}
            <div className="lg:hidden flex flex-col items-center gap-4">
              {STEPS.map((s, i) => (
                <Fragment key={s.title}>
                  <motion.div
                    initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="flex items-center gap-5 w-full max-w-sm"
                  >
                    <div className="relative shrink-0">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#E16F42] to-[#1F4989] flex items-center justify-center shadow-lg">
                        <s.icon className="w-7 h-7 text-white" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-[10px] font-bold">
                        {i + 1}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">
                        {s.title}
                      </h3>
                      <p className="text-sm text-white/45">{s.desc}</p>
                    </div>
                  </motion.div>
                  {i < STEPS.length - 1 && (
                    <motion.div
                      className="w-[2px] h-8 bg-gradient-to-b from-[#E16F42]/30 to-[#1F4989]/30 rounded-full"
                      initial={{ scaleY: 0 }}
                      whileInView={{ scaleY: 1 }}
                      viewport={{ once: true }}
                    />
                  )}
                </Fragment>
              ))}
            </div>
          </div>
        </section>

        <SectionDivider />

        {/* ═══ 4. Dashboard Preview ═══ */}
        <section className="relative py-24 md:py-32 overflow-hidden">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={staggerChildren}
              className="text-center mb-16 md:mb-20"
            >
              <motion.p
                variants={fadeInUp}
                className="text-sm uppercase tracking-[0.2em] text-[#E16F42] font-semibold mb-3"
              >
                Live Preview
              </motion.p>
              <motion.h2
                variants={fadeInUp}
                className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-5"
              >
                Dashboard at a Glance
              </motion.h2>
              <motion.p
                variants={fadeInUp}
                className="text-lg md:text-xl text-white/50 max-w-3xl mx-auto"
              >
                Floating panels give you instant visibility into your AI ecosystem
              </motion.p>
            </motion.div>

            <div
              className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8"
              style={{ perspective: 1200 }}
            >
              {/* Applications Panel */}
              <motion.div
                initial={{ opacity: 0, y: 60, rotateY: -5 }}
                whileInView={{ opacity: 1, y: 0, rotateY: -3 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                  whileHover={{ scale: 1.03, rotateY: 0, transition: { duration: 0.3 } }}
                  className="backdrop-blur-xl bg-white/[0.05] border border-white/[0.1] rounded-2xl overflow-hidden hover:shadow-[0_8px_50px_rgba(225,111,66,0.12)] transition-shadow duration-300"
                >
                  <div className="p-5 border-b border-white/[0.06]">
                    <div className="flex items-center gap-2">
                      <Layers className="w-5 h-5 text-[#E16F42]" />
                      <h4 className="font-semibold text-white">Applications</h4>
                    </div>
                  </div>
                  <div className="p-5 space-y-1">
                    {MOCK_APPS.map((app) => (
                      <div
                        key={app.name}
                        className="flex items-center justify-between py-2.5 border-b border-white/[0.04] last:border-0"
                      >
                        <span className="text-white/70 text-sm">{app.name}</span>
                        <StatusBadge status={app.status} />
                      </div>
                    ))}
                  </div>
                </motion.div>
              </motion.div>

              {/* Users Panel */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.15 }}
              >
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                  whileHover={{ scale: 1.03, transition: { duration: 0.3 } }}
                  className="backdrop-blur-xl bg-white/[0.05] border border-white/[0.1] rounded-2xl overflow-hidden lg:-mt-4 hover:shadow-[0_8px_50px_rgba(31,73,137,0.15)] transition-shadow duration-300"
                >
                  <div className="p-5 border-b border-white/[0.06]">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-[#4787c7]" />
                      <h4 className="font-semibold text-white">User Management</h4>
                    </div>
                  </div>
                  <div className="p-5 space-y-1">
                    {MOCK_USERS.map((user) => (
                      <div
                        key={user.name}
                        className="flex items-center justify-between py-2.5 border-b border-white/[0.04] last:border-0"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E16F42]/30 to-[#1F4989]/30 flex items-center justify-center text-[10px] font-bold text-white/60">
                            {user.initials}
                          </div>
                          <span className="text-white/70 text-sm">{user.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-white/30 text-xs">{user.role}</span>
                          <div
                            className={`w-1.5 h-1.5 rounded-full ${
                              user.status === "online"
                                ? "bg-emerald-400"
                                : "bg-amber-400"
                            }`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </motion.div>

              {/* Audit Logs Panel */}
              <motion.div
                initial={{ opacity: 0, y: 60, rotateY: 5 }}
                whileInView={{ opacity: 1, y: 0, rotateY: 3 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <motion.div
                  animate={{ y: [0, -7, 0] }}
                  transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                  whileHover={{ scale: 1.03, rotateY: 0, transition: { duration: 0.3 } }}
                  className="backdrop-blur-xl bg-white/[0.05] border border-white/[0.1] rounded-2xl overflow-hidden hover:shadow-[0_8px_50px_rgba(225,111,66,0.12)] transition-shadow duration-300"
                >
                  <div className="p-5 border-b border-white/[0.06]">
                    <div className="flex items-center gap-2">
                      <Activity className="w-5 h-5 text-[#E16F42]" />
                      <h4 className="font-semibold text-white">Audit Logs</h4>
                    </div>
                  </div>
                  <div className="p-5 space-y-1">
                    {MOCK_LOGS.map((log, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between py-2.5 border-b border-white/[0.04] last:border-0"
                      >
                        <div>
                          <span className="text-white/60 text-sm">{log.action}</span>
                          <span className="text-white/30 text-xs ml-2">
                            {log.detail}
                          </span>
                        </div>
                        <span className="text-white/25 text-xs shrink-0 ml-3">
                          {log.time}
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        <SectionDivider />

        {/* ═══ 5. Benefits Section ═══ */}
        <section className="relative py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={staggerChildren}
              className="text-center mb-16 md:mb-20"
            >
              <motion.p
                variants={fadeInUp}
                className="text-sm uppercase tracking-[0.2em] text-[#E16F42] font-semibold mb-3"
              >
                Energy Core
              </motion.p>
              <motion.h2
                variants={fadeInUp}
                className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-5"
              >
                Why AI Portal
              </motion.h2>
              <motion.p
                variants={fadeInUp}
                className="text-lg md:text-xl text-white/50 max-w-3xl mx-auto"
              >
                A living AI system radiating energy across your organization
              </motion.p>
            </motion.div>

            <div className="relative">
              {/* Central energy waves */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <motion.div
                  className="w-48 h-48 rounded-full blur-3xl"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(225,111,66,0.2), transparent)",
                  }}
                  animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.3, 0.5, 0.3],
                  }}
                  transition={{ duration: 4, repeat: Infinity }}
                />
                {[0, 1, 2].map((idx) => (
                  <motion.div
                    key={idx}
                    className="absolute w-32 h-32 rounded-full border border-white/[0.04]"
                    animate={{ scale: [1, 3.5], opacity: [0.25, 0] }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      delay: idx * 1.3,
                      ease: "easeOut",
                    }}
                  />
                ))}
              </div>

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={staggerChildren}
                className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
              >
                {BENEFITS.map((b) => (
                  <motion.div
                    key={b.title}
                    variants={fadeInUp}
                    whileHover={{
                      y: -6,
                      transition: { duration: 0.3 },
                    }}
                    className="backdrop-blur-xl bg-white/[0.04] border border-white/[0.08] rounded-2xl p-7 text-center hover:bg-white/[0.08] hover:border-white/[0.15] transition-colors duration-300 hover:shadow-[0_8px_40px_rgba(225,111,66,0.1)]"
                  >
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#E16F42]/20 to-[#1F4989]/20 flex items-center justify-center mx-auto mb-5 border border-white/[0.08]">
                      <b.icon className="w-6 h-6 text-[#E16F42]" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">
                      {b.title}
                    </h3>
                    <p className="text-sm text-white/45 leading-relaxed">
                      {b.desc}
                    </p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        <SectionDivider />

        {/* ═══ 6. CTA Section ═══ */}
        <section className="relative py-24 md:py-32">
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <motion.div
              className="w-[500px] h-[500px] rounded-full blur-3xl"
              style={{
                background:
                  "radial-gradient(circle, rgba(225,111,66,0.15), transparent 70%)",
              }}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.5, 0.3],
              }}
              transition={{ duration: 3, repeat: Infinity }}
            />
          </div>

          <div className="relative max-w-4xl mx-auto px-6 text-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={staggerChildren}
            >
              <motion.h2
                variants={fadeInUp}
                className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-6"
              >
                Start Building Your AI Ecosystem{" "}
                <span className="bg-gradient-to-r from-[#E16F42] to-white bg-clip-text text-transparent">
                  Today
                </span>
              </motion.h2>
              <motion.p
                variants={fadeInUp}
                className="text-lg md:text-xl text-white/50 mb-12 max-w-2xl mx-auto"
              >
                Join forward-thinking organizations already transforming their
                AI workflows
              </motion.p>
              <motion.div variants={scaleIn}>
                <motion.div
                  className="inline-block rounded-2xl"
                  animate={{
                    boxShadow: [
                      "0 0 30px rgba(225,111,66,0.25)",
                      "0 0 60px rgba(225,111,66,0.45)",
                      "0 0 30px rgba(225,111,66,0.25)",
                    ],
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Link
                    to="/auth/login"
                    className="group inline-flex items-center gap-3 bg-gradient-to-r from-[#E16F42] to-[#c4572e] text-white text-lg font-bold py-5 px-12 rounded-2xl hover:opacity-90 transition-all duration-300 hover:scale-105"
                  >
                    Get Started Now
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ═══ 7. Footer ═══ */}
        <footer className="relative py-12 border-t border-white/[0.06]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#E16F42] to-[#1F4989] shadow-[0_0_15px_rgba(225,111,66,0.3)]" />
                <span className="font-bold text-lg text-white">AI Portal</span>
              </div>
              <div className="flex items-center gap-8 text-sm text-white/30">
                <a
                  href="#"
                  className="hover:text-white/60 transition-colors duration-200"
                >
                  Privacy
                </a>
                <a
                  href="#"
                  className="hover:text-white/60 transition-colors duration-200"
                >
                  Terms
                </a>
                <a
                  href="#"
                  className="hover:text-white/60 transition-colors duration-200"
                >
                  Contact
                </a>
              </div>
              <p className="text-sm text-white/20">
                © {new Date().getFullYear()} AI Portal. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
